import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Alert, Button, Card, Result, Skeleton, Typography } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getPublicReport, publicPdfUrl, publicPhotoUrl, signPublicReport } from "./api";
import { formatDateTime } from "./format";
import ReportPreview from "./ReportPreview";
import { SignatureForm } from "./SignDialog";
import "./workReports.css";

/** The customer's page behind the signing link: read the report, sign it, download the PDF. */
const PublicSign = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = useParams();
  const domain = new URLSearchParams(useLocation().search).get("domain") || "";
  const [state, setState] = useState({ loading: true });
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getPublicReport(token, domain)
      .then(res => setState({ loading: false, view: res.data.data, expiresAt: res.data.expiresAt }))
      .catch(err =>
        setState({ loading: false, failed: err?.response?.data?.code || "LINK_INVALID" })
      );
  }, [token, domain]);

  const sign = async values => {
    setSigning(true);
    setError("");
    try {
      const res = await signPublicReport(token, domain, { name: values.name, image: values.image });
      setState(prev => ({ ...prev, view: res.data.data, justSigned: true }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(
        err?.response?.data?.errorMessage ||
          t("The signature could not be saved. Please try again.")
      );
    } finally {
      setSigning(false);
    }
  };

  const view = state.view;
  const signed = view && view.status !== "draft";

  return (
    <div className="work-reports-page wr-public" dir={i18n.dir?.()}>
      <div className="wr-public-inner">
        {state.loading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 10 }} />
          </Card>
        ) : state.failed ? (
          <Card>
            <Result
              status="warning"
              title={t("This link is not valid or has expired.")}
              subTitle={t("Please ask the company for a new link.")}
            />
          </Card>
        ) : (
          <>
            {view.seller?.name && (
              <div className="wr-public-company">
                {view.seller.logo ? <img src={view.seller.logo} alt="" /> : null}
                <Typography.Text strong>{view.seller.name}</Typography.Text>
              </div>
            )}
            {state.justSigned && (
              <Result
                status="success"
                title={t("Thank you, the report is signed.")}
                subTitle={t("You can download a copy as PDF.")}
                extra={
                  <Button
                    type="primary"
                    icon={<FilePdfOutlined />}
                    href={publicPdfUrl(token, domain)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Download PDF")}
                  </Button>
                }
              />
            )}
            {signed && !state.justSigned && (
              <Alert
                className="mb-3"
                type="info"
                showIcon
                message={t("This report has already been signed.")}
                action={
                  <Button
                    size="small"
                    href={publicPdfUrl(token, domain)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    PDF
                  </Button>
                }
              />
            )}
            <Card className="mb-3">
              <ReportPreview
                view={view}
                photoSrc={photoId => publicPhotoUrl(token, domain, photoId)}
              />
            </Card>
            {!signed && (
              <Card className="mb-3" title={t("Your signature")}>
                <Typography.Paragraph type="secondary">
                  {t("With your signature you confirm the work, times and materials listed above.")}
                </Typography.Paragraph>
                <SignatureForm
                  defaultName={view.customer?.contactName || view.customer?.name || ""}
                  showCopy={false}
                  showLocation={false}
                  submitting={signing}
                  onSubmit={sign}
                />
                {error && <Alert className="mt-3" type="error" showIcon message={error} />}
                {state.expiresAt && (
                  <Typography.Paragraph type="secondary" className="mt-3">
                    {t("Valid until {{date}}", {
                      date: formatDateTime(state.expiresAt, i18n.language)
                    })}
                  </Typography.Paragraph>
                )}
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PublicSign;
